import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { User, Store, Plus, Trash2, Search } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface Vendor {
  id: string;
  full_name: string;
  business_name: string | null;
  phone: string | null;
}

interface User {
  id: string;
  full_name: string;
  phone: string | null;
  role: string;
}

interface VendorManager {
  id: string;
  vendor_id: string;
  manager_id: string;
  role: string;
  vendor: Vendor;
  manager: User;
}

export const VendorManagersTab = () => {
  const { toast } = useToast();
  const [vendorManagers, setVendorManagers] = useState<VendorManager[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [addDialog, setAddDialog] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Récupérer les relations vendeur-gestionnaire
      const { data: vmData, error: vmError } = await supabase
        .from('vendor_managers')
        .select(`
          *,
          vendor:profiles!vendor_id(id, full_name, business_name, phone),
          manager:profiles!manager_id(id, full_name, phone, system_role)
        `)
        .order('created_at', { ascending: false });

      if (vmError) throw vmError;
      setVendorManagers(vmData || []);

      // Récupérer tous les vendeurs
      const { data: vendorsData, error: vendorsError } = await supabase
        .from('profiles')
        .select('id, full_name, business_name, phone')
        .eq('vendor_status', 'approved')
        .order('business_name');

      if (vendorsError) throw vendorsError;
      setVendors(vendorsData || []);

      // Récupérer tous les utilisateurs (tous les profils sauf le vendeur lui-même sera filtré au moment de la sélection)
      const { data: usersData, error: usersError } = await supabase
        .from('profiles')
        .select('id, full_name, phone, system_role')
        .order('full_name');

      if (usersError) throw usersError;
      setUsers(usersData || []);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const addManager = async () => {
    if (!selectedVendor || !selectedUser) {
      toast({
        title: 'Erreur',
        description: 'Veuillez sélectionner un vendeur et un utilisateur.',
        variant: 'destructive',
      });
      return;
    }

    // Vérifier si la relation existe déjà
    const existingRelation = vendorManagers.find(
      vm => vm.vendor_id === selectedVendor && vm.manager_id === selectedUser
    );

    if (existingRelation) {
      toast({
        title: 'Erreur',
        description: 'Cet utilisateur est déjà gestionnaire de ce vendeur.',
        variant: 'destructive',
      });
      return;
    }

    // Vérifier la limite de 5 gestionnaires
    const managerCount = vendorManagers.filter(vm => vm.vendor_id === selectedVendor).length;
    if (managerCount >= 5) {
      toast({
        title: 'Erreur',
        description: 'Un vendeur ne peut avoir plus de 5 gestionnaires.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('vendor_managers')
        .insert({
          vendor_id: selectedVendor,
          manager_id: selectedUser,
        });

      if (error) throw error;

      toast({
        title: 'Gestionnaire ajouté',
        description: 'Le gestionnaire a été assigné avec succès.',
      });

      setAddDialog(false);
      setSelectedVendor('');
      setSelectedUser('');
      fetchData();
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const removeManager = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir retirer ce gestionnaire ?')) return;

    try {
      const { error } = await supabase
        .from('vendor_managers')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Gestionnaire retiré',
        description: 'Le gestionnaire a été retiré avec succès.',
      });

      fetchData();
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const filteredManagers = vendorManagers.filter(vm =>
    vm.vendor.business_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    vm.vendor.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    vm.manager.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getManagerCount = (vendorId: string) => {
    return vendorManagers.filter(vm => vm.vendor_id === vendorId).length;
  };

  if (loading) {
    return <div className="flex justify-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-500 rounded-xl">
                <Store className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Vendeurs avec gestionnaires</p>
                <p className="text-2xl font-bold">
                  {new Set(vendorManagers.map(vm => vm.vendor_id)).size}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-green-500 rounded-xl">
                <User className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total gestionnaires</p>
                <p className="text-2xl font-bold">{vendorManagers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-purple-500 rounded-xl">
                <User className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Utilisateurs actifs</p>
                <p className="text-2xl font-bold">
                  {new Set(vendorManagers.map(vm => vm.manager_id)).size}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main content */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Gestion des Gestionnaires
              </CardTitle>
              <CardDescription>
                Assignez des gestionnaires aux comptes vendeurs
              </CardDescription>
            </div>
            <Dialog open={addDialog} onOpenChange={setAddDialog}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Assigner un gestionnaire
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Assigner un gestionnaire</DialogTitle>
                  <DialogDescription>
                    Sélectionnez un vendeur et un utilisateur pour créer une relation de gestion.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Vendeur</Label>
                    <Select value={selectedVendor} onValueChange={setSelectedVendor}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un vendeur" />
                      </SelectTrigger>
                      <SelectContent>
                        {vendors.map((vendor) => (
                          <SelectItem key={vendor.id} value={vendor.id}>
                            <div className="flex items-center justify-between w-full">
                              <span>{vendor.business_name || vendor.full_name}</span>
                              <Badge variant="secondary" className="ml-2">
                                {getManagerCount(vendor.id)}/5
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Utilisateur</Label>
                    <Select value={selectedUser} onValueChange={setSelectedUser}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un utilisateur" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.full_name} ({user.system_role})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAddDialog(false)}>
                    Annuler
                  </Button>
                  <Button onClick={addManager}>
                    Assigner
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Table */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vendeur</TableHead>
                <TableHead>Gestionnaire</TableHead>
                <TableHead>Téléphone</TableHead>
                <TableHead>Rôle organisationnel</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredManagers.map((vm) => (
                <TableRow key={vm.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">
                        {vm.vendor.business_name || vm.vendor.full_name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {vm.vendor.phone}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">{vm.manager.full_name}</p>
                  </TableCell>
                  <TableCell>{vm.manager.phone || 'N/A'}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{vm.manager.system_role}</Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeManager(vm.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredManagers.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              Aucune relation gestionnaire-vendeur trouvée.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};